import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Mail, Phone, Building2, CalendarClock, CheckCircle2, XCircle, Send, Loader2, TrendingUp, Trophy, Clock, Banknote } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  pending:  { color: 'bg-amber-100 text-amber-800 border-amber-200',   labelFr: 'En attente',  labelEn: 'Pending' },
  sent:     { color: 'bg-sky-100 text-sky-800 border-sky-200',         labelFr: 'Devis envoyé', labelEn: 'Quote sent' },
  accepted: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', labelFr: 'Accepté', labelEn: 'Accepted' },
  rejected: { color: 'bg-rose-100 text-rose-800 border-rose-200',      labelFr: 'Refusé',      labelEn: 'Rejected' },
};

export const VendorQuotesTab = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondQuote, setRespondQuote] = useState(null);
  const [form, setForm] = useState({ status: 'sent', quoted_total: '', valid_until: '', vendor_notes: '', unit_prices: {} });
  const [submitting, setSubmitting] = useState(false);
  const [pipeline, setPipeline] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [qRes, pRes] = await Promise.all([
        axios.get(`${API}/quotes/vendor`, authHeaders),
        axios.get(`${API}/quotes/vendor/pipeline`, authHeaders).catch(() => ({ data: null })),
      ]);
      setQuotes(qRes.data || []);
      setPipeline(pRes.data);
    } catch (err) {
      toast.error(T('Impossible de charger les devis', 'Failed to load quotes'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
     
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openRespond = (q) => {
    setRespondQuote(q);
    const initialUnitPrices = {};
    (q.items || []).forEach((it) => {
      initialUnitPrices[it.product_id] = q.quoted_unit_prices?.[it.product_id] ?? it.list_price ?? '';
    });
    setForm({
      status: 'sent',
      quoted_total: q.quoted_total ?? '',
      valid_until: q.valid_until ?? '',
      vendor_notes: q.vendor_notes ?? '',
      unit_prices: initialUnitPrices,
    });
  };

  const closeRespond = () => {
    setRespondQuote(null);
    setForm({ status: 'sent', quoted_total: '', valid_until: '', vendor_notes: '', unit_prices: {} });
  };

  const computeTotal = () => {
    if (!respondQuote) return 0;
    let total = 0;
    (respondQuote.items || []).forEach((it) => {
      const unit = parseFloat(form.unit_prices?.[it.product_id]);
      if (!isNaN(unit)) total += unit * (it.quantity || 0);
    });
    return total;
  };

  const submitResponse = async (statusOverride) => {
    if (!respondQuote) return;
    setSubmitting(true);
    try {
      const quotedTotal = parseFloat(form.quoted_total) || computeTotal();
      const unitPricesNum = {};
      Object.entries(form.unit_prices || {}).forEach(([k, v]) => {
        const n = parseFloat(v);
        if (!isNaN(n)) unitPricesNum[k] = n;
      });
      const payload = {
        status: statusOverride || form.status || 'sent',
        quoted_total: quotedTotal || null,
        quoted_unit_prices: unitPricesNum,
        valid_until: form.valid_until || null,
        vendor_notes: form.vendor_notes || null,
      };
      await axios.put(`${API}/quotes/${respondQuote.id}/respond`, payload, authHeaders);
      toast.success(T('Réponse envoyée au client', 'Response sent to customer'));
      closeRespond();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur lors de l\'envoi', 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6">
      {/* B2B Pipeline KPIs */}
      {pipeline && (
        <Card data-testid="b2b-pipeline-card" className="border-[#0F4C5C]/10 bg-gradient-to-br from-[#0F4C5C] to-[#0B3A47] text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <TrendingUp className="w-5 h-5 text-[#E9C46A]" />
              {T('Pipeline B2B', 'B2B Pipeline')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div data-testid="kpi-pending-value">
                <p className="text-xs uppercase tracking-wider text-white/60 flex items-center gap-1.5"><Banknote className="w-3 h-3" /> {T('Valeur en attente', 'Pending value')}</p>
                <p className="text-2xl font-bold text-[#E9C46A] mt-1">€{(pipeline.pending_pipeline_value || 0).toFixed(0)}</p>
                <p className="text-xs text-white/50 mt-0.5">{pipeline.pending_count} {T('devis ouverts', 'open quotes')}</p>
              </div>
              <div data-testid="kpi-sent-value">
                <p className="text-xs uppercase tracking-wider text-white/60 flex items-center gap-1.5"><Send className="w-3 h-3" /> {T('Offres en cours', 'Active offers')}</p>
                <p className="text-2xl font-bold text-sky-300 mt-1">€{(pipeline.sent_pipeline_value || 0).toFixed(0)}</p>
                <p className="text-xs text-white/50 mt-0.5">{pipeline.sent_count} {T('devis envoyés', 'quotes sent')}</p>
              </div>
              <div data-testid="kpi-conversion-rate">
                <p className="text-xs uppercase tracking-wider text-white/60 flex items-center gap-1.5"><Trophy className="w-3 h-3" /> {T('Taux de conversion', 'Conversion rate')}</p>
                <p className="text-2xl font-bold text-emerald-300 mt-1">{(pipeline.conversion_rate || 0).toFixed(0)}%</p>
                <p className="text-xs text-white/50 mt-0.5">{pipeline.converted_count} {T('gagnés', 'won')} / {pipeline.rejected_count} {T('perdus', 'lost')}</p>
              </div>
              <div data-testid="kpi-avg-response">
                <p className="text-xs uppercase tracking-wider text-white/60 flex items-center gap-1.5"><Clock className="w-3 h-3" /> {T('Délai moyen de réponse', 'Avg response time')}</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {pipeline.avg_response_time_hours !== null && pipeline.avg_response_time_hours !== undefined
                    ? (pipeline.avg_response_time_hours < 1
                        ? T('< 1h', '< 1h')
                        : pipeline.avg_response_time_hours < 24
                          ? `${pipeline.avg_response_time_hours.toFixed(1)}h`
                          : `${(pipeline.avg_response_time_hours / 24).toFixed(1)}j`)
                    : '—'}
                </p>
                <p className="text-xs text-white/50 mt-0.5">{T('Objectif < 24h', 'Target < 24h')}</p>
              </div>
            </div>
            {pipeline.recent_30d_quotes > 0 && (
              <p className="text-xs text-white/60 mt-4 pt-3 border-t border-white/10">
                {T('Activité 30 derniers jours :', '30-day activity:')} <strong className="text-white">{pipeline.recent_30d_quotes} {T('nouvelles demandes', 'new requests')}</strong>
                {' · '}{T('Valeur gagnée totale :', 'Total won value:')} <strong className="text-emerald-300">€{(pipeline.converted_value || 0).toFixed(0)}</strong>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card data-testid="vendor-quotes-tab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#0F4C5C]" />
            {T('Devis B2B reçus', 'B2B quotes received')}
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {quotes.length} {T('devis', 'quotes')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0F4C5C]" /></div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-[#0F4C5C]/15 rounded-xl bg-[#FBF8F3]">
            <FileText className="w-12 h-12 mx-auto text-[#0F4C5C]/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              {T('Aucune demande de devis reçue pour l\'instant.', 'No quote requests received yet.')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {T('Les demandes B2B des acheteurs professionnels apparaîtront ici.', 'B2B requests from pro buyers will appear here.')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {quotes.map((q) => {
              const badge = STATUS_BADGES[q.status] || STATUS_BADGES.pending;
              return (
                <div key={q.id} data-testid={`quote-card-${q.id}`} className="border border-[#0F4C5C]/10 rounded-xl p-4 hover:border-[#0F4C5C]/30 transition-colors bg-white">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="font-bold text-[#0F4C5C] flex items-center gap-1.5">
                          <Building2 className="w-4 h-4" />
                          {q.company_name}
                        </h3>
                        <Badge className={`${badge.color} border`}>{T(badge.labelFr, badge.labelEn)}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        <p className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {q.contact_email}</p>
                        {q.contact_phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {q.contact_phone}</p>}
                        <p className="flex items-center gap-1.5"><CalendarClock className="w-3 h-3" /> {T('Reçu le', 'Received on')} {formatDate(q.created_at)}</p>
                      </div>
                      <div className="mt-3 space-y-1.5">
                        {(q.items || []).map((it) => (
                          <div key={it.product_id} className="flex items-center justify-between text-sm bg-[#FBF8F3] rounded px-3 py-1.5">
                            <span className="text-[#0F4C5C]">
                              {it.name} <span className="text-muted-foreground">× {it.quantity}</span>
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {T('Catalogue', 'List')} €{(it.list_price || 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                      {q.additional_notes && (
                        <div className="mt-3 text-xs text-[#0F4C5C]/80 bg-amber-50 border-l-2 border-amber-300 px-3 py-2 rounded">
                          <strong>{T('Notes client : ', 'Customer notes: ')}</strong>{q.additional_notes}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      {q.status === 'pending' ? (
                        <Button size="sm" className="btn-primary" onClick={() => openRespond(q)} data-testid={`quote-respond-${q.id}`}>
                          <Send className="w-3.5 h-3.5 mr-1.5" />{T('Répondre', 'Respond')}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => openRespond(q)}>
                          {T('Voir détails', 'View details')}
                        </Button>
                      )}
                      {q.quoted_total && (
                        <p className="text-right text-xs">
                          <span className="text-muted-foreground">{T('Devis :', 'Quote:')}</span>{' '}
                          <strong className="text-[#0F4C5C]">€{q.quoted_total.toFixed(2)}</strong>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={!!respondQuote} onOpenChange={(open) => { if (!open) closeRespond(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#0F4C5C]" />
              {T('Répondre au devis', 'Respond to quote')}
            </DialogTitle>
            <DialogDescription>
              {respondQuote && `${respondQuote.company_name} · ${respondQuote.contact_email}`}
            </DialogDescription>
          </DialogHeader>

          {respondQuote && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">{T('Prix unitaires HT proposés', 'Proposed unit prices')}</Label>
                <div className="mt-2 space-y-2">
                  {(respondQuote.items || []).map((it) => (
                    <div key={it.product_id} className="grid grid-cols-12 gap-2 items-center bg-[#FBF8F3] rounded p-2">
                      <p className="col-span-7 text-sm text-[#0F4C5C]">{it.name}</p>
                      <p className="col-span-2 text-xs text-muted-foreground text-center">× {it.quantity}</p>
                      <div className="col-span-3 relative">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.unit_prices?.[it.product_id] ?? ''}
                          onChange={(e) => setForm((f) => ({ ...f, unit_prices: { ...f.unit_prices, [it.product_id]: e.target.value } }))}
                          placeholder={`${(it.list_price || 0).toFixed(2)}`}
                          className="h-9 pr-7"
                          data-testid={`quote-unit-price-${it.product_id}`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-right text-sm">
                  <span className="text-muted-foreground">{T('Total calculé HT :', 'Computed total:')}</span>{' '}
                  <strong className="text-[#0F4C5C] text-base">€{computeTotal().toFixed(2)}</strong>
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="quoted_total">{T('Total proposé HT (€)', 'Proposed total (€)')}</Label>
                  <Input
                    id="quoted_total"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.quoted_total}
                    onChange={(e) => setForm((f) => ({ ...f, quoted_total: e.target.value }))}
                    placeholder={computeTotal().toFixed(2)}
                    className="mt-1.5"
                    data-testid="quote-quoted-total"
                  />
                </div>
                <div>
                  <Label htmlFor="valid_until">{T('Valable jusqu\'au', 'Valid until')}</Label>
                  <Input
                    id="valid_until"
                    type="date"
                    value={form.valid_until ? form.valid_until.slice(0, 10) : ''}
                    onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
                    className="mt-1.5"
                    data-testid="quote-valid-until"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="vendor_notes">{T('Message au client (optionnel)', 'Message to customer (optional)')}</Label>
                <Textarea
                  id="vendor_notes"
                  value={form.vendor_notes}
                  onChange={(e) => setForm((f) => ({ ...f, vendor_notes: e.target.value }))}
                  placeholder={T('Délais de livraison, conditions de paiement, garanties...', 'Delivery times, payment terms, warranties...')}
                  rows={3}
                  className="mt-1.5"
                  data-testid="quote-vendor-notes"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {respondQuote?.status === 'pending' && (
              <Button
                variant="outline"
                onClick={() => submitResponse('rejected')}
                disabled={submitting}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="quote-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-1.5" />
                {T('Décliner', 'Decline')}
              </Button>
            )}
            <Button
              onClick={() => submitResponse('sent')}
              disabled={submitting}
              className="btn-primary"
              data-testid="quote-send-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              {T('Envoyer le devis', 'Send quote')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    </div>
  );
};
